import {
  Problem,
  type TestStats,
  type Content,
  type HasGuides,
  type HasRelatedContents,
  Cause,
  type Test,
  type TreelessDiagnostics,
  ComplexityLevel,
  MAX_COMPLEXITY_LEVEL,
  DEFAULT_COMPLEXITY_LEVEL,
  PriorityLevel,
  MAX_PRIORITY_LEVEL,
  DEFAULT_PRIORITY_LEVEL,
  ProblemType,
  DEFAULT_TREELESS_DIAGNOSTICS_CONFIG,
  type TreelessDiagnosticsConfig,
} from 'tsp-types'
import { MIN_COMPLEXITY_LEVEL, MIN_PRIORITY_LEVEL } from 'tsp-types/types'

const ETA = 0.1
const MIN_DIAGNOSTIC_SESSIONS = 10

export const TEST_STATUS_LIST = {
  PASSED: 'passed',
  FAILED: 'failed',
  CONDUCTED_REPAIR: 'conductedRepair',
  UN_NECESSARY: 'unnecessary',
  RECALIBRATE: 'recalibrate',
} as const

export type TEST_STATUS =
  (typeof TEST_STATUS_LIST)[keyof typeof TEST_STATUS_LIST]

type Comparator<T> = (item1: T, item2: T) => number

export function isWindowTreeless(): boolean {
  const params = new URLSearchParams(window.location.search)
  return params.get('treeless') === '1'
}

export function isTreelessWindowClosed(): boolean {
  if (isWindowTreeless()) {
    window.parent.postMessage('close', window.location.origin)
    return true
  }
  return false
}

export function setTreelessClass() {
  if (isWindowTreeless()) {
    document.documentElement.classList.add('treeless')
  }
}

export function getLinkEntitiesList(
  treelessData: TreelessDiagnostics | undefined,
  targetEntity: HasGuides | HasRelatedContents
) {
  const guides = 'guides' in targetEntity ? targetEntity.guides : []
  const relatedContents =
    'relatedContents' in targetEntity ? targetEntity.relatedContents : []

  const guidesList = Object.keys(guides ?? {})
    .map(guideId => treelessData?.guides.find(guide => guide._id === guideId))
    .filter(guide => guide !== undefined)
  const relatedContentList = Object.keys(relatedContents ?? {})
    .map(contentId =>
      treelessData?.contents.find(content => content._id === contentId)
    )
    .filter(content => content !== undefined)

  const contentMap = treelessData?.contents.reduce(
    (acc, content) => {
      acc[content._id] = content
      return acc
    },
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {} as Record<string, Content>
  )

  return { guidesList, relatedContentList, contentMap }
}

export function comparatorChain<T>(
  ...comparators: Comparator<T>[]
): Comparator<T> {
  return (item1: T, item2: T) => {
    for (const comparator of comparators) {
      const order = comparator(item1, item2)
      if (order !== 0) {
        return order
      }
    }
    return 0
  }
}

export interface NewTestScores {
  baseScore: number
  sessionRelevance: number
  score: number
}

export interface TestScores {
  problemScore: number
  causePriorityScore: number
  causeDifficultyScore: number
  testPriorityScore: number
  testDifficultyScore: number

  normalizeCausePriorityScore: number
  normalizeCauseDifficultyScore: number
  normalizeTestPriorityScore: number
  normalizeTestDifficultyScore: number

  failureRatioPercent?: number
  failureRatio?: number
}

export interface TestRank {
  averageScore: number
  rank: number
  totalScore: number
  normalizeTotalScore: number
}

interface TestLinks {
  test: Test
  causes: Record<string, Cause>
  problems: Record<string, Problem>
}

export interface TestInfo extends TestRank, NewTestScores, TestLinks {
  scores: TestScores
}

export function getTestsInfo(
  diagnostics: TreelessDiagnostics,
  resolvedProblemIds: Set<string>,
  config: TreelessDiagnosticsConfig = DEFAULT_TREELESS_DIAGNOSTICS_CONFIG
): Record<string, TestInfo> {
  const problemCount = diagnostics.problems.length - resolvedProblemIds.size
  if (problemCount <= 0) {
    return {}
  }

  const testLinks = buildDiagnosticTestLinks(diagnostics, resolvedProblemIds)
  const testScore = buildScoresForTests(
    Object.values(testLinks),
    problemCount,
    diagnostics.testStats
  )
  const averageScores = testAverageScore(testScore)
  const testRank = buildRankTests(testScore)
  const testTotalScore = calcTotalScore(testRank, averageScores)

  const totalScoreMax = Math.max(...Object.values(testTotalScore))
  const totalScoreMin = Math.min(...Object.values(testTotalScore))

  const averageScoreMax = Math.max(...Object.values(averageScores))
  const averageScoreMin = Math.min(...Object.values(averageScores))

  const dtcTotal = diagnostics.problems.filter(
    p => p.problemType === ProblemType.DTC && !resolvedProblemIds.has(p._id)
  ).length

  const symptomTotal = diagnostics.problems.filter(
    p => p.problemType === ProblemType.SYMPTOM && !resolvedProblemIds.has(p._id)
  ).length
  const newTestScore = calcNewTestScore(
    testScore,
    testLinks,
    dtcTotal,
    symptomTotal,
    config
  )

  const testInfo = Object.keys(testLinks).reduce(
    (acc, testId) => {
      const scores = testScore[testId]

      const scoreToSort = scoreScaleTransformation(
        normalizeScore(testTotalScore[testId], totalScoreMin, totalScoreMax),
        averageScoreMin,
        averageScoreMax
      )

      acc[testId] = {
        ...testLinks[testId],
        scores,
        rank: testRank[testId],
        averageScore: averageScores[testId],
        totalScore: testTotalScore[testId],
        normalizeTotalScore: scoreToSort,
        ...newTestScore[testId],
      }
      return acc
    },
    {} as Record<string, TestInfo>
  )
  return testInfo
}

export function recalculateTestScoresFromMetadata(
  originalScores: Record<string, TestScores>,
  updatedTests: Record<string, Pick<Test, 'priority' | 'complexity'>>
): Record<string, TestScores> {
  const updatedScores: Record<string, TestScores> = {}

  for (const [testId, original] of Object.entries(originalScores)) {
    const updated = updatedTests[testId]

    if (!updated) {
      // No change for this test
      updatedScores[testId] = original
      continue
    }

    const newPriorityScore = updated.priority ?? original.testPriorityScore
    const newDifficultyScore = getComplexityLevelOrDefault(updated.complexity)

    updatedScores[testId] = {
      ...original,
      testPriorityScore: newPriorityScore,
      testDifficultyScore: newDifficultyScore,
      // Update normalized scores as well
      normalizeTestPriorityScore: priorityNormalization(newPriorityScore),
      normalizeTestDifficultyScore: complexityNormalization(newDifficultyScore),
    }
  }

  return updatedScores
}

export function rebuildTestInfoFromUpdatedTests(
  currentTestInfo: Record<string, TestInfo>,
  updatedTests: Record<string, Pick<Test, 'priority' | 'complexity'>>
): Record<string, TestInfo> {
  // Step 1: Recalculate updated test scores
  const updatedScores = recalculateTestScoresFromMetadata(
    Object.fromEntries(
      Object.entries(currentTestInfo).map(([id, info]) => [id, info.scores])
    ),
    updatedTests
  )

  // Step 2: Recalculate average scores
  const averageScores = testAverageScore(updatedScores)

  // Step 3: Recalculate rank and total score
  const testRank = buildRankTests(updatedScores)
  const totalScore = calcTotalScore(testRank, averageScores)

  // Step 4: Normalize total score
  const totalScoreMax = Math.max(...Object.values(totalScore))
  const totalScoreMin = Math.min(...Object.values(totalScore))
  const averageScoreMax = Math.max(...Object.values(averageScores))
  const averageScoreMin = Math.min(...Object.values(averageScores))

  const result: Record<string, TestInfo> = {}

  for (const testId of Object.keys(currentTestInfo)) {
    const old = currentTestInfo[testId]
    result[testId] = {
      ...old,
      scores: updatedScores[testId],
      rank: testRank[testId],
      averageScore: averageScores[testId],
      totalScore: totalScore[testId],
      normalizeTotalScore: scoreScaleTransformation(
        normalizeScore(totalScore[testId], totalScoreMin, totalScoreMax),
        averageScoreMin,
        averageScoreMax
      ),
    }
  }

  return result
}

function normalizeScore(value: number, min: number, max: number) {
  return min === max ? value : (value - min) / (max - min)
}

function normalizationByMaximum(value: number, max: number, reverse = false) {
  if (reverse) {
    return (max - value + 1) / max
  }
  return value / max
}

const priorityNormalization = (priority?: number) => {
  const level = getPriorityLevelOrDefault(priority)
  return normalizationByMaximum(level, MAX_PRIORITY_LEVEL, true)
}

const complexityNormalization = (complexity?: number) => {
  const level = getPriorityLevelOrDefault(complexity)
  return normalizationByMaximum(level, MAX_COMPLEXITY_LEVEL, true)
}

function scoreScaleTransformation(
  normalizeValue: number,
  newMin: number,
  newMax: number
) {
  return normalizeValue * (newMax - newMin) + newMin
}

function calcTotalScore(
  ranks: Record<string, number>,
  averageScore: Record<string, number>
) {
  return Object.keys(ranks).reduce(
    (acc, testId) => {
      acc[testId] = ranks[testId] + (averageScore[testId] ?? 0)
      return acc
    },
    {} as Record<string, number>
  )
}

function buildScoresForTests(
  testLinks: TestLinks[],
  problemsCount: number,
  testStats?: TestStats
) {
  const isFailureRatioStatsExist =
    testStats !== undefined && Object.keys(testStats).length > 0
  return Object.values(testLinks).reduce(
    (acc, info) => {
      const problemScore = Object.keys(info.problems).length / problemsCount
      const causePriorityScore = Math.min(
        ...Object.values(info.causes).map(c =>
          getPriorityLevelOrDefault(c.priority)
        )
      )
      const causeDifficultyScore = Math.min(
        ...Object.values(info.causes).map(c =>
          getComplexityLevelOrDefault(c.complexity)
        )
      )
      const testPriorityScore = getPriorityLevelOrDefault(info.test.priority)
      const testDifficultyScore = getComplexityLevelOrDefault(
        info.test.complexity
      )
      const failureRatio = isFailureRatioStatsExist
        ? calcTestFailureRatio(info, testStats)
        : undefined
      const testScores: TestScores = {
        problemScore,

        causePriorityScore,
        causeDifficultyScore,
        testPriorityScore,
        testDifficultyScore,

        normalizeCausePriorityScore: priorityNormalization(causePriorityScore),
        normalizeCauseDifficultyScore:
          complexityNormalization(causeDifficultyScore),
        normalizeTestPriorityScore: priorityNormalization(testPriorityScore),
        normalizeTestDifficultyScore:
          complexityNormalization(testDifficultyScore),
        failureRatio: failureRatio,
        failureRatioPercent:
          failureRatio !== undefined ? failureRatio * 100 : undefined,
      }

      acc[info.test._id] = testScores
      return acc
    },
    {} as Record<string, TestScores>
  )
}

function calcTestFailureRatio(testInfo: TestLinks, testStats?: TestStats) {
  if (testStats === undefined) {
    return 0
  }
  const stats = testStats[testInfo.test._id]
  if (stats === undefined) {
    return 0
  }

  const failureRatios: number[] = [0]

  for (const pId of Object.keys(stats.problems)) {
    if (
      pId in testInfo.problems &&
      stats.problems[pId].totalDiagnosticSessions >= MIN_DIAGNOSTIC_SESSIONS
    ) {
      const failedCount =
        stats.problems[pId].statuses[TEST_STATUS_LIST.FAILED] ??
        stats.problems[pId].statuses[TEST_STATUS_LIST.CONDUCTED_REPAIR] ??
        0
      failureRatios.push(
        failedCount / stats.problems[pId].totalDiagnosticSessions
      )
    }
  }
  return Math.max(...failureRatios)
}

function buildDiagnosticTestLinks(
  diagnostics: TreelessDiagnostics,
  resolvedProblemIds: Set<string>
): Record<string, TestLinks> {
  const problems = keyBy(diagnostics.problems, problem => problem._id)
  const testIndex = keyBy(diagnostics.tests, test => test._id)

  return diagnostics.causes.reduce(
    (acc, cause) => {
      Object.keys(cause.problems ?? {})
        .filter(
          problemId =>
            !resolvedProblemIds.has(problemId) && problemId in problems
        )
        .forEach(problemId => {
          const linkedTests = Object.keys(cause.tests ?? {})
            .filter(testId => testId in testIndex)
            .map(testId => testIndex[testId])
          linkedTests.forEach(test => {
            const testInfo = acc[test._id] ?? {
              test,
              causes: {},
              problems: {},
            }
            testInfo.causes[cause._id] = cause

            testInfo.problems[problemId] = problems[problemId]
            acc[test._id] = testInfo
          })
        })
      return acc
    },
    {} as Record<string, TestLinks>
  )
}

function buildRankTests(testScores: Record<string, TestScores>) {
  const compareFn = comparatorChain<TestScores>(
    scoreComparator.bind(null, scores => scores.problemScore),
    scoreComparator.bind(null, scores => scores.failureRatio ?? 0),
    scoreComparator.bind(null, scores => scores.normalizeCausePriorityScore),
    scoreComparator.bind(null, scores => scores.normalizeCauseDifficultyScore),
    scoreComparator.bind(null, scores => scores.normalizeTestPriorityScore),
    scoreComparator.bind(null, scores => scores.normalizeTestDifficultyScore)
  )

  const sortedTests = Object.entries(testScores).sort(
    ([, test1Score], [, test2Score]) => compareFn(test1Score, test2Score)
  )
  let currentRank = 1
  let currentIndex = 0
  let prevTest: TestScores | undefined = undefined
  const rankTests: Record<string, number> = {}

  do {
    const currentTest = sortedTests[currentIndex] ?? []
    currentIndex++
    if (prevTest === undefined || compareFn(prevTest, currentTest[1]) === 0) {
      rankTests[currentTest[0]] = currentRank
    } else {
      currentRank++
      rankTests[currentTest[0]] = currentRank
    }
    prevTest = currentTest[1]
  } while (currentIndex < sortedTests.length)
  return rankTests
}

function keyBy<T>(array: T[], mapKey: (item: T) => string): Record<string, T> {
  return array.reduce(
    (acc, item) => {
      acc[mapKey(item)] = item
      return acc
    },
    {} as Record<string, T>
  )
}

function scoreComparator(
  scoreFn: (scores: TestScores) => number,
  score1: TestScores,
  score2: TestScores
) {
  return scoreFn(score1) - scoreFn(score2)
}

function averageScore(...scores: number[]) {
  return scores.reduce((sum, value) => sum + value, 0) / scores.length
}

function testAverageScore(tests: Record<string, TestScores>) {
  const scoreNames: (keyof TestScores)[] = [
    'problemScore',
    'failureRatio',
    'normalizeCausePriorityScore',
    'normalizeCauseDifficultyScore',
    'normalizeTestPriorityScore',
    'normalizeTestDifficultyScore',
  ]

  return Object.entries(tests).reduce(
    (acc, [testId, scores]) => {
      acc[testId] = averageScore(
        ...scoreNames
          .map(scoreName => scores[scoreName])
          .filter(val => val !== undefined)
      )
      return acc
    },
    {} as Record<string, number>
  )
}

export const getComplexityLevelOrDefault = (
  level?: number | null | ComplexityLevel,
  def?: ComplexityLevel
): ComplexityLevel => {
  if (
    level === undefined ||
    level === null ||
    !Number.isInteger(level) ||
    level <= 0 ||
    level > MAX_COMPLEXITY_LEVEL
  ) {
    return def ?? DEFAULT_COMPLEXITY_LEVEL
  }
  return level as ComplexityLevel
}

export const getPriorityLevelOrDefault = (
  level?: number | null | PriorityLevel,
  def?: PriorityLevel
): PriorityLevel => {
  if (
    level === undefined ||
    level === null ||
    !Number.isInteger(level) ||
    level <= 0 ||
    level > MAX_PRIORITY_LEVEL
  ) {
    return def ?? DEFAULT_PRIORITY_LEVEL
  }
  return level as PriorityLevel
}

export function calcNewTestScore(
  testScores: Record<string, TestScores>,
  testLinks: Record<string, TestLinks>,
  dtcTotal: number,
  symptomTotal: number,
  config: TreelessDiagnosticsConfig = DEFAULT_TREELESS_DIAGNOSTICS_CONFIG
): Record<string, NewTestScores> {
  return Object.keys(testScores).reduce(
    (result, testId) => {
      const scores = testScores[testId]
      const links = testLinks[testId]
      const priorityFactor = geometricMean(
        normalizeToOpenUnitRange(
          scores.testPriorityScore,
          MIN_PRIORITY_LEVEL,
          MAX_PRIORITY_LEVEL
        ),
        normalizeToOpenUnitRange(
          scores.causePriorityScore,
          MIN_PRIORITY_LEVEL,
          MAX_PRIORITY_LEVEL
        )
      )

      const difficultyFactor = geometricMean(
        normalizeToOpenUnitRange(
          scores.testDifficultyScore,
          MIN_COMPLEXITY_LEVEL,
          MAX_COMPLEXITY_LEVEL
        ),
        normalizeToOpenUnitRange(
          scores.causeDifficultyScore,
          MIN_COMPLEXITY_LEVEL,
          MAX_COMPLEXITY_LEVEL
        )
      )

      const fieldHitRate = 1 - (scores.failureRatio ?? 0)
      const baseScore =
        1 -
        fieldHitRate *
          (ETA + (1 - ETA) * geometricMean(priorityFactor, difficultyFactor))

      const dtcCount = Object.values(links.problems).filter(
        p => p.problemType === ProblemType.DTC
      ).length
      const symptomCount = Object.values(links.problems).filter(
        p => p.problemType === ProblemType.SYMPTOM
      ).length

      const sessionRelevance = calcTestSessionRelevance(
        dtcCount,
        dtcTotal,
        symptomCount,
        symptomTotal,
        { dtc: config.dtcWeight, symptom: config.symptomWeight }
      )

      result[testId] = {
        baseScore,
        sessionRelevance,
        score: applyJordanLift(
          baseScore,
          sessionRelevance,
          config.tau,
          config.k
        ),
      }

      return result
    },
    {} as Record<string, NewTestScores>
  )
}

export function geometricMean(...values: number[]): number {
  if (values.length === 0) return Number.NaN
  if (values.some(v => v <= 0)) return Number.NaN // geometric mean is undefined for non-positive numbers
  const product = values.reduce((acc, v) => acc * v, 1)
  return Math.pow(product, 1 / values.length)
}

export function normalizeToOpenUnitRange(
  value: number,
  min: number,
  max: number,
  epsilon = 0.05
): number {
  const ratio = (value - min) / (max - min)
  return epsilon + (1 - 2 * epsilon) * ratio
}

export function clip(x: number, min: number, max: number) {
  return Math.min(Math.max(x, min), max)
}

const DEFAULT_TEST_SESSION_RELEVANCE_WEIGHTS = {
  dtc: 0.5,
  symptom: 0.5,
}

export function calcTestSessionRelevance(
  coveredDTCs: number,
  totalDTCs: number,
  coveredSymptoms: number,
  totalSymptoms: number,
  weights?: { dtc: number; symptom: number }
): number {
  const { dtc: weightDTC, symptom: weightSymptom } = {
    ...DEFAULT_TEST_SESSION_RELEVANCE_WEIGHTS,
    ...weights,
  }

  const relevanceDTC = totalDTCs > 0 ? coveredDTCs / totalDTCs : 0
  const relevanceSymptom =
    totalSymptoms > 0 ? coveredSymptoms / totalSymptoms : 0

  const relevanceCore =
    weightDTC * relevanceDTC + weightSymptom * relevanceSymptom

  // Bonus for multiple DTC coverage
  const dtcCountBonus = Math.min(0.1 * (coveredDTCs - 1), 0.3)

  return clip(relevanceCore + dtcCountBonus, 0, 1)
}

export function applyJordanLift(
  baseScore: number,
  coverageR: number,
  tau = 0.5,
  k = 3.0
): number {
  const r = clip(coverageR, 0, 1)
  const base = clip(baseScore, 0, 1)
  const lift = Math.exp(k * (r - tau))

  return base * lift
}

const COMPLETED_TEST_STATUSES = new Set<TEST_STATUS>([
  TEST_STATUS_LIST.PASSED,
  TEST_STATUS_LIST.FAILED,
  TEST_STATUS_LIST.CONDUCTED_REPAIR,
  TEST_STATUS_LIST.UN_NECESSARY,
])

export interface TestListSortable {
  _id: string
  testInfo?: { normalizeTotalScore?: number }
}

export function getTestListSortScore(
  test: TestListSortable,
  calibratedTestRecords: Record<string, { scoreDependencyBased?: number }> = {}
): number {
  return (
    calibratedTestRecords[test._id]?.scoreDependencyBased ??
    test.testInfo?.normalizeTotalScore ??
    0
  )
}

export function isCompletedTestStatus(
  testId: string,
  finishedIds: Record<string, { status?: TEST_STATUS }>,
  calibratedTestRecords: Record<string, { status?: TEST_STATUS }>
): boolean {
  const status =
    finishedIds[testId]?.status ?? calibratedTestRecords[testId]?.status
  return status !== undefined && COMPLETED_TEST_STATUSES.has(status)
}

export function getNextHighestScoreTest<T extends TestListSortable>(
  tests: T[],
  options: {
    finishedIds?: Record<string, { status?: TEST_STATUS }>
    calibratedTestRecords?: Record<
      string,
      { scoreDependencyBased?: number; status?: TEST_STATUS }
    >
    excludeTestId?: string
  } = {}
): T | undefined {
  const {
    finishedIds = {},
    calibratedTestRecords = {},
    excludeTestId,
  } = options

  let highest: T | undefined
  let highestScore = Number.NEGATIVE_INFINITY

  for (const test of tests) {
    if (test._id === excludeTestId) continue
    if (isCompletedTestStatus(test._id, finishedIds, calibratedTestRecords)) {
      continue
    }
    const score = getTestListSortScore(test, calibratedTestRecords)
    if (score > highestScore) {
      highestScore = score
      highest = test
    }
  }

  return highest
}
